import {BoltOutlined} from '@mui/icons-material';
import {Box, Typography} from '@mui/material';
import {GridColumns} from '@mui/x-data-grid';
import {ChangeEvent, FC, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useLocation, useNavigate} from 'react-router-dom';
import {transactionsApi} from 'src/api';
import {chargingSessionsTableColumns} from 'src/components/chargingSessions/ChargingSessionsTable/ChargingSessionsTable.tableConfig';
import {ChargingSessionsTableCard} from 'src/components/chargingSessions/ChargingSessionsTable/ChargingSessionsTableCard';
import {ResponsiveTable} from 'src/components/common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {EmptyTable} from 'src/components/tables/EmptyTable';
import {Table} from 'src/components/tables/Table';
import {FilterBar} from 'src/components/common/FilterBar';
import {TableLayout} from 'src/components/tables/TableLayout';
import {TransactionSearchRequestData} from 'src/types';

interface InvoiceTransactionsProps {
    invoiceId: number;
    businessId: number;
}

export const InvoiceTransactions: FC<InvoiceTransactionsProps> = ({invoiceId, businessId}) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const currentLocation = useLocation();

    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(15);

    const [trigger, response] = transactionsApi.useLazyGetTransactionsQuery();

    useEffect(() => {
        const data: TransactionSearchRequestData = {
            businessId,
            invoiceId,
        };
        void trigger({data, params: {page, size: pageSize}});
    }, [invoiceId, businessId, page, pageSize]);

    const handleChangePage = (newPage: number) => setPage(newPage);
    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => {
        setPageSize(+e.target.value || 15);
        setPage(0);
    };

    const tableData = response.data?.content ?? [];
    const totalPages = response.data?.page?.totalPages ?? 0;
    const businessArr: GridColumns = [];

    const handleRowClick = (params: any) => {
        navigate(`/app/charging-sessions/${params.row.id}`, {
            state: {backTo: currentLocation.pathname + currentLocation.search},
        });
    };

    return (
        <Box>
            <TableLayout
                toolbar={(
                    <FilterBar
                        title={intl.formatMessage({id: 'invoiceLinkedTransactions'})}
                        icon={<BoltOutlined/>}
                        subtitle={intl.formatMessage({id: 'rowsFound'}, {count: tableData.length})}
                    />
                )}
                totalPages={totalPages}
                page={page}
                pageSize={pageSize}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
                <ResponsiveTable
                    desktopElement={
                        tableData.length ? (
                            <Table
                                rows={tableData}
                                hideFooter
                                getRowId={(row) => row?.id}
                                columns={chargingSessionsTableColumns(businessArr, intl)}
                                onRowClick={handleRowClick}
                                sx={{
                                    '& .MuiDataGrid-row': {
                                        cursor: 'pointer',
                                    },
                                }}
                            />
                        ) : (
                            <EmptyTable/>
                        )
                    }
                    responsiveElement={
                        tableData.length ? (
                            <CardList>
                                {tableData.map((item) => (
                                    <Box
                                        key={item.id}
                                        onClick={() =>
                                            navigate(`/app/charging-sessions/${item.id}`, {
                                                state: {backTo: currentLocation.pathname + currentLocation.search},
                                            })
                                        }
                                        sx={{cursor: 'pointer'}}
                                    >
                                        <ChargingSessionsTableCard data={item}/>
                                    </Box>
                                ))}
                            </CardList>
                        ) : (
                            <EmptyTable/>
                        )
                    }
                />
            </TableLayout>
        </Box>
    );
};
